/**
 * The borough's five stations (1970). Everything about a station's sound is derived from its seed:
 * the songs, the DJ's voice, the jingles, the adverts and the news.
 */

export type StationId = 'soul' | 'beat' | 'easy' | 'talk' | 'ska';

/** Musical style of a music station ('talk' has none). */
export type Style = 'soul' | 'beat' | 'easy' | 'ska';

export interface StationDef {
  id: StationId;
  name: string;
  tagline: string;
  seed: number;
  style: Style | null;
  /** Output trim (linear) so the stations sit at the same loudness (measured offline). */
  trim: number;
  /** Receiver bandwidth: lowpass corner in the car (Hz). Pirates on medium wave are narrower. */
  bandwidth: number;
  /** Off-air hiss level (linear, pre-trim): pirates and AM crackle more. */
  hiss: number;
  /** Runs adverts between records. */
  adverts: boolean;
  /** DJ voice character: pitch (Hz), pace (syllables/s), formant scale (1 = average), breathiness. */
  dj: { f0: number; pace: number; formant: number; breath: number };
}

export const STATIONS: readonly StationDef[] = [
  {
    id: 'soul',
    name: 'Marlow Soul 98.4',
    tagline: 'Slow roads, smooth grooves',
    seed: 0x5011,
    style: 'soul',
    trim: 1.45,
    bandwidth: 4800,
    hiss: 0.012,
    adverts: true,
    dj: { f0: 108, pace: 5.2, formant: 0.95, breath: 0.35 },
  },
  {
    id: 'beat',
    name: 'Heavy Pennant FM',
    tagline: 'Beat, mod and the Pennant sound',
    seed: 0xf0c4,
    style: 'beat',
    trim: 1.3,
    bandwidth: 6500,
    hiss: 0.006,
    adverts: true,
    dj: { f0: 132, pace: 7, formant: 1.05, breath: 0.2 },
  },
  {
    id: 'easy',
    name: 'Kessmouth Light 93.1',
    tagline: 'Light orchestral and easy listening',
    seed: 0xea5e,
    style: 'easy',
    trim: 1.55,
    bandwidth: 7500,
    hiss: 0.004,
    adverts: false,
    dj: { f0: 118, pace: 4.4, formant: 1, breath: 0.25 },
  },
  {
    id: 'talk',
    name: 'Borough 1071 Talk',
    tagline: 'News, your calls, the Kessmouth Evening Post',
    seed: 0x7a1c,
    style: null,
    trim: 1.3,
    bandwidth: 4200,
    hiss: 0.01,
    adverts: true,
    dj: { f0: 122, pace: 5.6, formant: 1, breath: 0.3 },
  },
  {
    id: 'ska',
    name: 'Radio Gullet 259',
    tagline: 'Pirate ska and reggae from a boat in the basin',
    seed: 0xb10e,
    style: 'ska',
    trim: 1.35,
    bandwidth: 4200,
    hiss: 0.016,
    adverts: true,
    dj: { f0: 146, pace: 6.4, formant: 1.08, breath: 0.3 },
  },
];

export function stationDef(id: StationId): StationDef {
  return STATIONS.find((s) => s.id === id) as StationDef;
}

/** A stable 32-bit seed for `(station, label, n)`. */
export function stationSeed(id: StationId, label: number, n: number): number {
  const def = stationDef(id);
  let h = (def.seed ^ Math.imul(label + 1, 0x9e3779b1)) >>> 0;
  h = (h ^ Math.imul(n + 0x51, 0x85ebca6b)) >>> 0;
  h ^= h >>> 15;
  return Math.imul(h, 0xc2b2ae35) >>> 0;
}
